import Export16 from '@carbon/icons-react/es/export/16';
import { Tooltip } from 'antd';
import naturalCompare from 'natural-compare-lite';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { FormattedMessage } from 'react-intl';
import { useHistory, useParams } from 'react-router';
import { Link } from 'react-router-dom';
import { Category } from 'api/timesheets/categories';
import { Configuration } from 'api/timesheets/configuration/configuration';
import { TimesheetReportsApi } from 'api/timesheets/reports/api';
import { NoCategory, TimeEntryCategoryId } from 'api/timesheets/reports/types';
import { Breadcrumb } from 'components/atoms/breadcrumb';
import { Button } from 'components/atoms/button';
import { Option } from 'components/atoms/select/types';
import { Page } from 'components/molecules/page';
import { CustomRangePicker } from 'components/organisms/projects-report/common';
import { getFormattedValueFromDateRange } from 'components/organisms/projects-report/common/range-picker/types';
import { ReportEmptyState } from 'components/organisms/timesheets/common/report-empty-state';
import { ReportLoadingState } from 'components/organisms/timesheets/common/report-loading-state';
import { CategorySelect } from 'components/organisms/timesheets/common/title-select';
import {
  categoryWiseReportTabs,
  TimesheetCategoryWiseReport as TimesheetCategoryWiseReportContent,
  TimesheetReportTab,
} from 'components/organisms/timesheets/reports';
import { useNextQueryParam } from 'components/organisms/timesheets/reports/use-next-query-param';
import { useTab } from 'components/organisms/timesheets/reports/use-tab';
import { Argon } from 'components/templates';
import { useTranslation } from 'hooks/use-translation';
import { RangeOptions, useDateRangeFilter } from '../use-date-range-filter';
import { useExport } from '../use-export';
import { useTimesheetReportSummary } from '../use-timesheet-report-summary';

const {
  Header: { Title },
} = Page;

interface Props {
  timesheetCategories: Category[];
  configuration: Configuration;
}
export const TimesheetCategoryWiseReport: FC<Props> = ({
  timesheetCategories,
  configuration,
}) => {
  const { push } = useHistory();
  const { selectedTab, getNextTab } = useTab(categoryWiseReportTabs);
  const { dateRange, setDateRange, getNext } = useDateRangeFilter();
  const { categoryId: _paramCategoryId } = useParams<{ categoryId: string }>();

  const categoryId = parseInt(_paramCategoryId, 10);

  const summaryApi = useCallback(() => {
    const [startDate, endDate] = getFormattedValueFromDateRange(dateRange);

    return TimesheetReportsApi.getCategoryWiseSummary(
      categoryId || null,
      startDate,
      endDate,
    );
  }, [dateRange, categoryId]);

  const { reportSummary, isSummaryLoading } =
    useTimesheetReportSummary(summaryApi);

  const categoryOptions: Option[] = useMemo(
    () =>
      [{ value: 0, label: 'No category' }].concat(
        timesheetCategories
          .map((category) => ({
            value: category.id,
            label: category.name,
            extra: { deleted: category.deleted },
          }))
          .sort((a, b) => naturalCompare(a.label, b.label)),
      ),
    [timesheetCategories],
  );

  const [currentSelectedCategory, setCurrentSelectedCategory] =
    useState<Option | undefined>();

  useEffect(() => {
    setCurrentSelectedCategory(
      categoryOptions.find((option) => option.value === categoryId),
    );
  }, [categoryId, categoryOptions, setCurrentSelectedCategory]);

  const currentCategory = useMemo(
    () => timesheetCategories.find((category) => category.id === categoryId),
    [timesheetCategories, categoryId],
  );
  const { exportAsCSV } = useExport({
    range: dateRange,
    param: categoryId === 0 ? NoCategory() : TimeEntryCategoryId(categoryId),
  });

  const isReportAvailable =
    !reportSummary ||
    reportSummary.billableMinutes + reportSummary.nonBillableMinutes !== 0;
  const { getNextQueryParam } = useNextQueryParam();

  return (
    <>
      <Helmet>
        <title>
          {`${currentSelectedCategory?.label} | ${useTranslation(
            'reports.timesheets.title',
          )}`}
        </title>
      </Helmet>
      <Argon
        breadcrumbs={
          <Breadcrumb.Item>
            <Link
              to={`/reports/timesheets${getNextQueryParam(
                dateRange,
              )}${getNextTab(TimesheetReportTab.Category)}`}
            >
              <FormattedMessage id="reports.timesheets.title" />
            </Link>
          </Breadcrumb.Item>
        }
        title={
          <Title>
            <CategorySelect
              options={categoryOptions}
              selected={currentSelectedCategory}
              onChange={(selected) => {
                push(
                  `/reports/timesheets/categories/${selected?.value}${getNext({
                    range: dateRange,
                  })}${getNextTab(selectedTab)}`,
                );
              }}
            />
          </Title>
        }
        actions={{
          primary: (
            <CustomRangePicker
              range={dateRange}
              onDateChange={setDateRange}
              rangeOptions={RangeOptions}
            />
          ),
          secondary: isReportAvailable ? (
            <Tooltip
              title={
                <FormattedMessage id="timesheets.reports.tooltips.export" />
              }
              placement="right"
            >
              <Button
                icon={<Export16 />}
                type="text"
                size="small"
                onClick={exportAsCSV}
              />
            </Tooltip>
          ) : null,
        }}
        content={
          !isSummaryLoading ? (
            <>
              {isReportAvailable ? (
                <TimesheetCategoryWiseReportContent
                  summary={reportSummary!}
                  timesheetCategories={timesheetCategories}
                  configuration={configuration}
                  dateRange={dateRange}
                  category={currentCategory}
                />
              ) : (
                <ReportEmptyState />
              )}
            </>
          ) : (
            <ReportLoadingState />
          )
        }
      />
    </>
  );
};
